import { useState, useEffect } from "react";
import { Upload, CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NONE = "__none__";

export default function ImportFleetDialog({ open, onOpenChange, onDone }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [fields, setFields] = useState([]);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [analysis, setAnalysis] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/import/vehicle-fields").then((r) => setFields(r.data)).catch(() => {});
      setStep(1); setFile(null); setPreview(null); setMapping({}); setAnalysis(null);
    }
  }, [open]);

  const guess = (fieldsList, columns) => {
    const map = {};
    fieldsList.forEach((f) => {
      const hit = columns.find((c) => {
        const cl = c.toLowerCase();
        return cl === f.key || cl.includes(f.key) || cl.includes(f.label.toLowerCase());
      });
      map[f.key] = hit || NONE;
    });
    return map;
  };

  const handleFile = async (f) => {
    if (!f) return;
    setFile(f); setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setMapping(guess(fields, data.columns));
      setStep(2);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const run = async (commit) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    const clean = {}; Object.entries(mapping).forEach(([k, v]) => { if (v && v !== NONE) clean[k] = v; });
    fd.append("mapping", JSON.stringify(clean));
    fd.append("commit", commit ? "true" : "false");
    try {
      const { data } = await api.post("/import/vehicles", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (commit) {
        toast.success(`${data.inserted} véhicule(s) importé(s)`);
        onOpenChange(false); onDone && onDone();
      } else {
        setAnalysis(data); setStep(3);
      }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Upload className="h-5 w-5" />Importer une flotte</DialogTitle></DialogHeader>

        {/* stepper */}
        <div className="mb-2 flex items-center gap-2 text-xs">
          {["Fichier", "Correspondance", "Validation"].map((s, i) => (
            <div key={s} className={`flex items-center gap-2 ${step >= i + 1 ? "text-[#001A3B]" : "text-[#8695AB]"}`}>
              <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ${step >= i + 1 ? "bg-[#A5894E] text-white" : "bg-[#EDF0F5]"}`}>{i + 1}</span>
              {s}{i < 2 && <ArrowRight className="h-3 w-3" />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="py-4">
            <Label className="mb-1.5 block text-sm">Fichier Excel (.xlsx) ou CSV</Label>
            <Input type="file" accept=".xlsx,.csv" onChange={(e) => handleFile(e.target.files[0])} data-testid="import-file-input" />
            <p className="mt-2 text-xs text-[#8695AB]">Colonnes attendues (au minimum) : Immatriculation, Marque. Les doublons d'immatriculation seront ignorés.</p>
            {busy && <p className="mt-3 text-sm text-[#001A3B]">Analyse du fichier…</p>}
          </div>
        )}

        {step === 2 && preview && (
          <div className="py-2">
            <p className="mb-3 text-sm text-[#4A5568]">{preview.total} ligne(s) détectée(s). Associez chaque champ à une colonne du fichier.</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {fields.map((f) => (
                <div key={f.key} className="flex items-center gap-2">
                  <Label className="w-36 shrink-0 text-xs text-[#4A5568]">{f.label}{f.required && <span className="text-red-500"> *</span>}</Label>
                  <Select value={mapping[f.key] || NONE} onValueChange={(v) => setMapping({ ...mapping, [f.key]: v })}>
                    <SelectTrigger data-testid={`import-map-${f.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>— Ignorer —</SelectItem>
                      {preview.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <div className="mt-4 overflow-x-auto rounded-lg border border-[#E2E8F0]">
              <table className="w-full text-xs">
                <thead><tr className="bg-[#F5F7FA] text-left text-[#4A5568]">{preview.columns.map((c) => <th key={c} className="px-2 py-1.5 font-semibold">{c}</th>)}</tr></thead>
                <tbody>{preview.sample.map((r, i) => <tr key={i} className="border-t border-[#EDF0F5]">{preview.columns.map((c) => <td key={c} className="px-2 py-1.5">{String(r[c] ?? "")}</td>)}</tr>)}</tbody>
              </table>
            </div>
          </div>
        )}

        {step === 3 && analysis && (
          <div className="py-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4"><CheckCircle2 className="h-5 w-5 text-emerald-600" /><div className="mt-2 font-heading text-2xl font-bold text-emerald-800">{analysis.valid}</div><div className="text-xs text-emerald-700">À importer</div></div>
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4"><AlertTriangle className="h-5 w-5 text-amber-600" /><div className="mt-2 font-heading text-2xl font-bold text-amber-800">{analysis.duplicates}</div><div className="text-xs text-amber-700">Doublons ignorés</div></div>
              <div className="rounded-xl border border-red-200 bg-red-50 p-4"><AlertTriangle className="h-5 w-5 text-red-600" /><div className="mt-2 font-heading text-2xl font-bold text-red-800">{analysis.errors.length}</div><div className="text-xs text-red-700">Erreurs</div></div>
            </div>
            {analysis.errors.length > 0 && (
              <div className="mt-3 max-h-32 overflow-y-auto rounded-lg border border-[#E2E8F0] p-2 text-xs text-[#4A5568]">
                {analysis.errors.map((e, i) => <div key={i}>Ligne {e.row} : {e.error}</div>)}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 2 && <Button variant="outline" onClick={() => setStep(1)}>Retour</Button>}
          {step === 3 && <Button variant="outline" onClick={() => setStep(2)}>Retour</Button>}
          {step === 2 && <Button onClick={() => run(false)} disabled={busy || !mapping.registration || mapping.registration === NONE} className="bg-[#001A3B] hover:bg-[#0A2A52]" data-testid="import-analyze-btn">{busy ? "Analyse…" : "Analyser"}</Button>}
          {step === 3 && <Button onClick={() => run(true)} disabled={busy || analysis.valid === 0} className="bg-[#A5894E] hover:bg-[#8F7440]" data-testid="import-confirm-btn">{busy ? "Import…" : `Importer ${analysis.valid} véhicule(s)`}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
